import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import SidebarPanel from '../admin/SidebarPanel';
import { adminApi } from '../../services/api';
import { selectLeadsData } from '../redux/slices/sales-module/salesLeadsSlice';
import { selectOpportunitiesData } from '../redux/slices/sales-module/salesOpportunitySlice';
import LeadAndOpportunityTracker from './shared/LeadAndOpportunityTracker';
import ProspectManager from './ProspectManager';

/**
 * EntityEditor - Create or edit leads/opportunities (Single Page Card Layout)
 * Opens in a sidebar panel with all sections in scrollable cards
 *
 * @param {string} mode - 'create' or 'edit'
 * @param {string} entityId - The ID of the entity to edit (only for edit mode)
 * @param {Object} entity - The entity data (optional, will fetch from Redux if not provided)
 * @param {boolean} isOpen - Whether the panel is open
 * @param {Function} onClose - Callback when panel is closed
 * @param {Function} onSave - Callback when entity is saved
 * @param {string} entityType - 'lead' or 'opportunity' (optional, will auto-detect from URL)
 */
const EntityEditor = ({
  mode = 'edit',
  entityId,
  entity: entityProp,
  isOpen,
  onClose,
  onSave,
  entityType: entityTypeProp,
  onNavigate = null,
  currentIndex = -1,
  totalEvents = 0
}) => {
  const location = useLocation();
  const [conflicts, setConflicts] = useState(null);
  const [checkingConflicts, setCheckingConflicts] = useState(false);
  const [showAllConflicts, setShowAllConflicts] = useState(false);
  const [loadedFromConflict, setLoadedFromConflict] = useState(null); // {type, id, name}
  const [isNavigating, setIsNavigating] = useState(false);
  const navigateTimerRef = useRef(null);

  // Collapsible section states (all expanded by default)
  const [expandedSections, setExpandedSections] = useState({
    organizationContact: true,
    prospectInfo: true,
    assignmentMetrics: true,
    jobDetails: true,
    organizationDetails: true,
    additionalInfo: true,
    addressInfo: true,
    systemInfo: true,
    activityHistory: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Auto-detect entity type from URL if not provided
  const entityType = useMemo(() => {
    if (entityTypeProp) return entityTypeProp;

    // Check URL path to determine entity type
    if (location.pathname.includes('/admin/sales/leads')) {
      return 'lead';
    } else if (location.pathname.includes('/admin/sales/opportunities')) {
      return 'opportunity';
    }
  }, [entityTypeProp, location.pathname]);

  // Try to get entity from prop first, then fall back to Redux
  const leadsData = useSelector(selectLeadsData);
  const opportunitiesData = useSelector(selectOpportunitiesData);

  const entity = useMemo(() => {
    if (mode === 'create') return null;

    // If entity passed as prop, use it
    if (entityProp) {
      return entityProp;
    }

    // Otherwise search in Redux slices
    return leadsData.find(p => p.id === entityId) ||
           opportunitiesData.find(p => p.id === entityId);
  }, [mode, entityProp, leadsData, opportunitiesData, entityId]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [formData, setFormData] = useState({});
  const [fullEntityData, setFullEntityData] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [contactStatuses, setContactStatuses] = useState([]);
  const [prospects, setProspects] = useState([]); // For create mode prospect management

  // Fetch dropdown options when panel opens
  useEffect(() => {
    if (isOpen) {
      // Fetch organizations
      adminApi.getOrganizations()
        .then(res => {
          const orgs = res.data.organizations || res.data || [];
          setOrganizations(orgs);
        })
        .catch(err => {
          setError('Failed to load organizations');
        });

      // Fetch contact statuses
      adminApi.getLeadOpportunityStatuses()
        .then(res => {
          const statusData = res.data?.results || res.data || [];
          setContactStatuses(Array.isArray(statusData) ? statusData : []);
        })
        .catch(err => console.error('Failed to load contact statuses:', err));
    }
  }, [isOpen, entityType]);

  // Fetch full entity data when panel opens (list view has lightweight data)
  useEffect(() => {
    if (mode === 'edit' && entityId && isOpen && entityType) {
      setIsLoading(true);
      const endpoint = entityType === 'lead'
        ? `/admin/sales/leads/${entityId}/`
        : `/admin/sales/opportunities/${entityId}/`;

      adminApi.get(endpoint)
        .then(response => {
          setFullEntityData(response.data);
          setIsLoading(false);
        })
        .catch(err => {
          setError(`Failed to load ${entityType} details`);
          setIsLoading(false);
          // Fall back to Redux data if fetch fails
          setFullEntityData(entity);
        });
    } else if (mode === 'create' && isOpen) {
      // Initialize empty form for create mode
      setFullEntityData({});
      setFormData({
        is_active: true,
        is_qualified: false
      });
      setIsLoading(false);
    }
  }, [mode, entityId, isOpen, entityType, entity]);

  // Initialize form data when full entity data is loaded
  useEffect(() => {
    if (mode === 'edit' && fullEntityData && isOpen) {
      const initialData = extractFormData(fullEntityData);
      setFormData(initialData);
    }
  }, [mode, fullEntityData, isOpen]);

  // Reset state when panel closes
  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setSuccessMessage('');
      setLoadedFromConflict(null);
      if (mode === 'create') {
        setFormData({ is_active: true, is_qualified: false });
        setProspects([]); // Reset prospects for create mode
      }
    }
  }, [isOpen, mode]);

  // Extract data from entity (including lead_data/opportunity_data JSONField)
  const extractFormData = (data) => {
    let extracted = { ...data };

    // Convert nested FK objects to IDs for form fields
    // Organization uses organization_id for writes (serializer write_only field)
    if (extracted.organization && typeof extracted.organization === 'object') {
      extracted.organization_id = extracted.organization.id;
    } else if (extracted.organization) {
      extracted.organization_id = extracted.organization;
    }

    if (extracted.assigned_to && typeof extracted.assigned_to === 'object') {
      extracted.assigned_to = extracted.assigned_to.id;
    }
    if (extracted.prospect && typeof extracted.prospect === 'object') {
      extracted.prospect = extracted.prospect.id;
    }
    if (extracted.contact_status && typeof extracted.contact_status === 'object') {
      extracted.contact_status = extracted.contact_status.id;
    }
    if (extracted.priority && typeof extracted.priority === 'object') {
      extracted.priority = extracted.priority.id;
    }
    if (extracted.source && typeof extracted.source === 'object') {
      extracted.source = extracted.source.id;
    }
    if (extracted.lead_type && typeof extracted.lead_type === 'object') {
      extracted.lead_type = extracted.lead_type.id;
    }

    // These are model-level FK/relational fields. lead_data/opportunity_data JSONFields
    // may contain keys with the same names but raw string values (e.g. source="paycom").
    // Never let the JSONField overwrite the actual model field values.
    const protectedModelFields = [
      'source', 'lead_type', 'contact_status', 'priority',
      'assigned_to', 'organization', 'organization_id', 'prospect',
    ];

    // Extract from lead_data or opportunity_data JSONField if present
    if (data.lead_data && Object.keys(data.lead_data).length > 0) {
      Object.entries(data.lead_data).forEach(([key, value]) => {
        if (!protectedModelFields.includes(key)) {
          extracted[key] = value;
        }
      });
    } else if (data.opportunity_data && Object.keys(data.opportunity_data).length > 0) {
      Object.entries(data.opportunity_data).forEach(([key, value]) => {
        if (!protectedModelFields.includes(key)) {
          extracted[key] = value;
        }
      });
    }

    // Extract nested company_object
    if (extracted.company_object) {
      Object.keys(extracted.company_object).forEach(key => {
        extracted[`company_${key}`] = extracted.company_object[key];
      });
    }

    // Extract first location
    if (extracted.locations && extracted.locations[0]) {
      Object.keys(extracted.locations[0]).forEach(key => {
        extracted[`location_${key}`] = extracted.locations[0][key];
      });
    }

    // Map tracking_notes to notes field for editing
    if (extracted.tracking_notes && !extracted.notes) {
      extracted.notes = extracted.tracking_notes;
    }

    return extracted;
  };

  // Conflict check with debouncing
  const checkForConflicts = useCallback(async (currentFormData) => {
    if (mode !== 'create') return; // Only check for new entries

    const checkData = {
      entity_type: entityType,
      company_name: currentFormData.company_name || '',
      company_domain: currentFormData.company_domain || '',
      company_url: currentFormData.company_url || '',
      job_title: currentFormData.job_title || '',
      prospect_email: currentFormData.prospect_email || '',
      prospect_name: currentFormData.prospect_name || ''
    };

    // Only check if we have at least one meaningful field
    const hasData = Object.values(checkData).some(val => val && val !== entityType);
    if (!hasData) {
      setConflicts(null);
      return;
    }

    try {
      setCheckingConflicts(true);
      const response = await adminApi.post('/admin/sales/fast-search/', checkData);

      if (response.data?.success && response.data?.has_conflicts) {
        setConflicts(response.data.matches);
      } else {
        setConflicts(null);
      }
    } catch (err) {
      setConflicts(null);
    } finally {
      setCheckingConflicts(false);
    }
  }, [mode, entityType]);

  // Debounce conflict check
  useEffect(() => {
    if (mode !== 'create' || !isOpen) return;

    // Don't search again if we've already loaded data from a conflict
    if (loadedFromConflict) return;

    const timeoutId = setTimeout(() => {
      checkForConflicts(formData);
    }, 800); // 800ms debounce

    return () => clearTimeout(timeoutId);
  }, [formData, mode, isOpen, loadedFromConflict, checkForConflicts]);

  // Handle clicking on a conflict entry to populate form fields
  const handleConflictClick = async (conflictType, conflictId, conflictName) => {
    try {
      setIsLoading(true);
      setError(null);

      let fetchedData = null;
      const uniqueKey = `${conflictType.toLowerCase()}-${conflictId}`;

      if (conflictType === 'Prospect') {
        // Fetch prospect data
        const response = await adminApi.get(`/admin/sales/prospects/${conflictId}/`);
        fetchedData = response.data;

        // Populate prospect-related fields
        setFormData(prev => ({
          ...prev,
          prospect: fetchedData.id,
          // Add any other prospect fields available
        }));
      } else if (conflictType === 'Lead') {
        // Fetch full lead data
        const response = await adminApi.get(`/admin/sales/leads/${conflictId}/`);
        fetchedData = response.data;

        // Only populate company and location data, not job-specific fields
        const companyAndLocationData = {};

        // Extract company_object fields
        if (fetchedData.company_object) {
          Object.keys(fetchedData.company_object).forEach(key => {
            companyAndLocationData[`company_${key}`] = fetchedData.company_object[key];
          });
        } else if (fetchedData.lead_data?.company_object) {
          Object.keys(fetchedData.lead_data.company_object).forEach(key => {
            companyAndLocationData[`company_${key}`] = fetchedData.lead_data.company_object[key];
          });
        }

        // Extract location fields
        if (fetchedData.locations && fetchedData.locations[0]) {
          Object.keys(fetchedData.locations[0]).forEach(key => {
            companyAndLocationData[`location_${key}`] = fetchedData.locations[0][key];
          });
        } else if (fetchedData.lead_data?.locations && fetchedData.lead_data.locations[0]) {
          Object.keys(fetchedData.lead_data.locations[0]).forEach(key => {
            companyAndLocationData[`location_${key}`] = fetchedData.lead_data.locations[0][key];
          });
        }

        // Populate organization if available
        if (fetchedData.organization) {
          companyAndLocationData.organization_id = typeof fetchedData.organization === 'object'
            ? fetchedData.organization.id
            : fetchedData.organization;
        }

        setFormData(prev => ({
          ...prev,
          ...companyAndLocationData,
          // Keep is_active and is_qualified as defaults for create mode
          is_active: true,
          is_qualified: false
        }));
      } else if (conflictType === 'Opportunity') {
        // Fetch full opportunity data
        const response = await adminApi.get(`/admin/sales/opportunities/${conflictId}/`);
        fetchedData = response.data;

        // Only populate company and location data, not job-specific fields
        const companyAndLocationData = {};

        // Extract company_object fields
        if (fetchedData.company_object) {
          Object.keys(fetchedData.company_object).forEach(key => {
            companyAndLocationData[`company_${key}`] = fetchedData.company_object[key];
          });
        } else if (fetchedData.opportunity_data?.company_object) {
          Object.keys(fetchedData.opportunity_data.company_object).forEach(key => {
            companyAndLocationData[`company_${key}`] = fetchedData.opportunity_data.company_object[key];
          });
        }

        // Extract location fields
        if (fetchedData.locations && fetchedData.locations[0]) {
          Object.keys(fetchedData.locations[0]).forEach(key => {
            companyAndLocationData[`location_${key}`] = fetchedData.locations[0][key];
          });
        } else if (fetchedData.opportunity_data?.locations && fetchedData.opportunity_data.locations[0]) {
          Object.keys(fetchedData.opportunity_data.locations[0]).forEach(key => {
            companyAndLocationData[`location_${key}`] = fetchedData.opportunity_data.locations[0][key];
          });
        }

        // Populate organization if available
        if (fetchedData.organization) {
          companyAndLocationData.organization_id = typeof fetchedData.organization === 'object'
            ? fetchedData.organization.id
            : fetchedData.organization;
        }

        setFormData(prev => ({
          ...prev,
          ...companyAndLocationData,
          // Keep is_active as default for create mode
          is_active: true
        }));
      }

      // Track which conflict was loaded for visual feedback
      setLoadedFromConflict({ type: conflictType, id: conflictId, name: conflictName, uniqueKey });

      // Hide conflicts after loading data
      setConflicts(null);

    } catch (err) {
      console.error(`Error loading ${conflictType} data:`, err);
      setError(`Failed to load ${conflictType} data`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Convert to appropriate type based on field
    let finalValue = type === 'checkbox' ? checked : value;

    // Convert empty strings to null for FK fields
    const fkFields = ['organization_id', 'source', 'lead_type', 'contact_status', 'priority', 'assigned_to', 'prospect'];
    if (fkFields.includes(name) && finalValue === '') {
      finalValue = null;
    }

    setFormData(prev => ({
      ...prev,
      [name]: finalValue
    }));
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsLoading(true);
      setError(null);
      setSuccessMessage('');

      // Client-side validation: organization selection is mandatory when user has multiple organizations
      if (mode === 'create' && organizations.length > 1 && !formData.organization_id) {
        setError('You have access to multiple organizations. Please select the one that will own this lead/opportunity.');
        setExpandedSections(prev => ({ ...prev, additionalInfo: true }));
        setTimeout(() => {
          const orgField = document.querySelector('select[name="organization_id"]');
          if (orgField) {
            orgField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            orgField.focus();
          }
        }, 300);
        setIsLoading(false);
        return;
      }

      let response;

      if (mode === 'create') {
        // Build create payload
        const createPayload = buildCreatePayload(formData);

        // Call the appropriate API based on entity type
        if (entityType === 'lead') {
          response = await adminApi.post('/admin/sales/leads/', createPayload);
        } else if (entityType === 'opportunity') {
          response = await adminApi.post('/admin/sales/opportunities/', createPayload);
        }

        if (response.data && response.data.success !== false) {
          const entityName = entityType === 'lead' ? 'Lead' : 'Opportunity';
          setSuccessMessage(`${entityName} created successfully`);

          // Scroll to top to show success message
          window.scrollTo({ top: 0, behavior: 'smooth' });

          // Call parent onSave callback with the created data
          if (onSave) {
            onSave(response.data);
          }
        } else {
          setError(response.data?.message || `Failed to create ${entityType}`);
        }
      } else {
        // Build update payload
        const updatedEntity = buildUpdatePayload(formData);

        // Call the appropriate API based on entity type
        if (entityType === 'lead') {
          response = await adminApi.updateSalesLead(entity.id, updatedEntity);
        } else if (entityType === 'opportunity') {
          response = await adminApi.updateSalesOpportunity(entity.id, updatedEntity);
        }

        if (response.data && response.data.success !== false) {
          const entityName = entityType === 'lead' ? 'Lead' : 'Opportunity';
          setSuccessMessage(`${entityName} updated successfully`);

          // Scroll to top to show success message
          window.scrollTo({ top: 0, behavior: 'smooth' });

          // Call parent onSave callback with the updated data from response
          if (onSave) {
            onSave(response.data);
          }

          // Keep panel open - user can close manually or navigate to next
        } else {
          setError(response.data?.message || `Failed to update ${entityType}`);
        }
      }
    } catch (err) {
      console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} ${entityType}:`, err);
      setError(err.response?.data?.message || err.message || `An error occurred while ${mode === 'create' ? 'creating' : 'updating'} ${entityType}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle navigation (prev/next)
  const handleNavigate = (direction) => {
    if (isNavigating) return;
    setIsNavigating(true);
    if (onNavigate) {
      onNavigate(direction);
    }
    // Re-enable after 400ms to let the remount settle
    clearTimeout(navigateTimerRef.current);
    navigateTimerRef.current = setTimeout(() => setIsNavigating(false), 400);
  };

  // Build create payload from form data
  const buildCreatePayload = (data) => {
    const payload = {};

    // Direct model fields
    // NOTE: source, lead_type, and is_qualified only exist on SalesLead, not SalesOpportunity
    // NOTE: organization_id is the write field for organization (serializer write_only)
    const directFields = [
      'external_id', 'notes', 'is_active', 'lead_score',
      'contact_status', 'priority',
      'assigned_to', 'organization_id', 'prospect',
      'last_contacted_date', 'follow_up_date'
    ];

    // Add lead-specific fields ONLY for leads (they don't exist on opportunities)
    if (entityType === 'lead') {
      directFields.push('source', 'lead_type', 'is_qualified');
    }

    directFields.forEach(field => {
      if (data[field] !== undefined && data[field] !== null && data[field] !== '') {
        payload[field] = data[field];
      }
    });

    // Build company_object from company_* fields
    const companyData = {};
    const locationData = {};

    Object.keys(data).forEach(key => {
      if (key.startsWith('company_')) {
        const companyKey = key.replace('company_', '');
        if (data[key]) {
          companyData[companyKey] = data[key];
        }
      } else if (key.startsWith('location_')) {
        const locationKey = key.replace('location_', '');
        if (data[key]) {
          locationData[locationKey] = data[key];
        }
      }
    });

    // Build lead_data or opportunity_data JSONField
    const jsonFieldData = {};

    // Add job-related fields
    const jobFields = [
      'job_title', 'description', 'location', 'short_location', 'date_posted',
      'salary_string', 'remote', 'hybrid', 'url', 'employment_type'
    ];

    jobFields.forEach(field => {
      if (data[field] !== undefined && data[field] !== null && data[field] !== '') {
        jsonFieldData[field] = data[field];
      }
    });

    // Note: Prospect data is now handled via M2M relationship, not JSONField
    // The backend serializer will create prospects from the prospects array

    // Add company object and locations
    if (Object.keys(companyData).length > 0) {
      jsonFieldData.company_object = companyData;
      // Also add company name directly
      if (companyData.name) {
        jsonFieldData.company = companyData.name;
      }
    }

    if (Object.keys(locationData).length > 0) {
      jsonFieldData.locations = [locationData];
    }

    // Add the JSONField to payload
    if (entityType === 'lead') {
      payload.lead_data = jsonFieldData;
    } else {
      payload.opportunity_data = jsonFieldData;
    }

    // Add prospects M2M data (for backend to create ProspectRelation records)
    if (prospects && prospects.length > 0) {
      payload.prospects = prospects.map(p => ({
        // If prospect already exists (attached from search), just send the ID
        ...(p._isNew ? {
          full_name: p.full_name,
          first_name: p.first_name,
          last_name: p.last_name,
          email: p.email,
          title: p.title
        } : {
          id: p.id
        }),
        // M2M relation metadata
        is_primary: p.relation?.is_primary || false,
        role: p.relation?.role || 'Contact'
      }));
    }

    return payload;
  };

  // Build update payload from form data
  const buildUpdatePayload = (data) => {
    const payload = {};

    // Model-level fields that can be updated directly
    const modelFields = [
      'external_id', 'notes', 'is_active', 'lead_score',
      'contact_status', 'priority',
      'assigned_to', 'organization_id', 'prospect',
      'last_contacted_date', 'follow_up_date', 'tracking_notes'
    ];

    // Add lead-specific fields ONLY for leads
    if (entityType === 'lead') {
      modelFields.push('source', 'lead_type', 'is_qualified');
    }

    // Only add model fields that actually changed
    modelFields.forEach(field => {
      const newValue = data[field];

      // For organization, compare ID (fullEntityData has nested object)
      if (field === 'organization_id') {
        const oldOrgId = fullEntityData?.organization?.id || fullEntityData?.organization;
        if (newValue !== oldOrgId && newValue !== undefined) {
          payload[field] = newValue;
        }
        return;
      }

      // For FK fields, compare IDs
      if (['contact_status', 'priority', 'assigned_to', 'prospect', 'source', 'lead_type'].includes(field)) {
        const oldValue = fullEntityData?.[field];
        const oldId = typeof oldValue === 'object' ? oldValue?.id : oldValue;
        if (newValue !== oldId && newValue !== undefined) {
          payload[field] = newValue;
        }
        return;
      }

      // For regular fields, direct comparison
      const oldValue = fullEntityData?.[field];
      if (newValue !== oldValue && newValue !== undefined) {
        payload[field] = newValue;
      }
    });

    // Detect changes to company_*/location_* fields
    const companyData = {};
    const locationData = {};
    let companyChanged = false;
    let locationChanged = false;

    Object.keys(data).forEach(key => {
      if (key.startsWith('company_')) {
        const companyKey = key.replace('company_', '');
        const newValue = data[key];
        const oldValue = fullEntityData?.company_object?.[companyKey] ||
                        (entityType === 'lead' ? fullEntityData?.lead_data?.company_object?.[companyKey] :
                         fullEntityData?.opportunity_data?.company_object?.[companyKey]);

        if (newValue !== oldValue) {
          companyChanged = true;
        }
        // Preserve value if not empty
        if (newValue !== null && newValue !== '') {
          companyData[companyKey] = newValue;
        } else if (oldValue !== undefined && oldValue !== null && oldValue !== '') {
          companyData[companyKey] = oldValue;
        }
      } else if (key.startsWith('location_')) {
        const locationKey = key.replace('location_', '');
        const newValue = data[key];
        const oldValue = fullEntityData?.locations?.[0]?.[locationKey] ||
                        (entityType === 'lead' ? fullEntityData?.lead_data?.locations?.[0]?.[locationKey] :
                         fullEntityData?.opportunity_data?.locations?.[0]?.[locationKey]);

        if (newValue !== oldValue) {
          locationChanged = true;
        }
        // Preserve value if not empty
        if (newValue !== null && newValue !== '') {
          locationData[locationKey] = newValue;
        } else if (oldValue !== undefined && oldValue !== null && oldValue !== '') {
          locationData[locationKey] = oldValue;
        }
      }
    });

    // Job-related fields in JSONField
    const jsonFieldFields = [
      'job_title', 'description', 'location', 'short_location', 'date_posted',
      'salary_string', 'remote', 'hybrid', 'url', 'employment_type',
      'prospect_name', 'prospect_email', 'prospect_phone', 'prospect_job_title'
    ];

    // Detect changes to JSONField fields
    let jsonFieldChanged = companyChanged || locationChanged;
    const changedJsonFields = {};

    jsonFieldFields.forEach(field => {
      const newValue = data[field];
      const oldValue = entityType === 'lead'
        ? fullEntityData?.lead_data?.[field]
        : fullEntityData?.opportunity_data?.[field];

      if (newValue !== oldValue && newValue !== undefined) {
        jsonFieldChanged = true;
        changedJsonFields[field] = newValue;
      }
    });

    // Only send JSONField if something changed
    if (jsonFieldChanged) {
      const existingData = entityType === 'lead'
        ? (fullEntityData?.lead_data || {})
        : (fullEntityData?.opportunity_data || {});

      const updatedJsonField = { ...existingData, ...changedJsonFields };

      // Update company_object if changed
      if (companyChanged) {
        updatedJsonField.company_object = {
          ...(existingData.company_object || {}),
          ...companyData
        };
        if (companyData.name) {
          updatedJsonField.company = companyData.name;
        }
      }

      // Update locations if changed
      if (locationChanged) {
        updatedJsonField.locations = [{
          ...(existingData.locations?.[0] || {}),
          ...locationData
        }];
      }

      if (entityType === 'lead') {
        payload.lead_data = updatedJsonField;
      } else {
        payload.opportunity_data = updatedJsonField;
      }
    }

    return payload;
  };

  // Determine title based on mode and entity type
  const basePanelTitle = mode === 'create'
    ? (entityType === 'lead' ? 'Create Lead' : 'Create Opportunity')
    : (entityType === 'lead' ? 'Edit Lead' : 'Edit Opportunity');

  const canNavigatePrev = onNavigate && currentIndex > 0;
  const canNavigateNext = onNavigate && currentIndex >= 0 && currentIndex < totalEvents - 1;

  const renderTitle = () => (
    <div className="d-flex align-items-center justify-content-between w-100">
      {basePanelTitle}
      {onNavigate && totalEvents > 0 && (
        <div className="d-flex align-items-center gap-2 ms-3">
          <span className="text-muted small">
            {currentIndex + 1} of {totalEvents}
          </span>
          <div className="btn-group btn-group-sm">
            <button
              className="btn btn-outline-secondary"
              onClick={() => handleNavigate('prev')}
              disabled={!canNavigatePrev || isNavigating}
              title="Previous"
            >
              <i className="fas fa-chevron-left"></i>
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={() => handleNavigate('next')}
              disabled={!canNavigateNext || isNavigating}
              title="Next"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <SidebarPanel
      isOpen={isOpen}
      onClose={onClose}
      title={renderTitle()}
      showCloseButton={true}
      className="prospect-form-editor"
      style={{ width: '900px', maxWidth: '95vw' }}
    >
      <form onSubmit={handleSubmit}>
        {/* Status Alerts */}
        {error && (
          <div className="alert alert-danger alert-sm mb-2" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}
        {successMessage && (
          <div className="alert alert-success alert-sm mb-2" role="alert">
            <i className="fas fa-check-circle me-2"></i>
            {successMessage}
          </div>
        )}

        {/* Company & Organization Section */}
        <div className="card p-3 mb-2">
          <div
            className="d-flex card-title text-primary justify-content-between align-items-center mb-2 pt-2"
            style={{ cursor: 'pointer' }}
            onClick={() => toggleSection('organizationContact')}
          >
            <span>Company</span>
            <i className={`fas fa-chevron-${expandedSections.organizationContact ? 'up' : 'down'}`}></i>
          </div>
          {expandedSections.organizationContact && (
            <div className="">
              <div className="mb-2">
                {/* <label className="form-label form-label-sm mb-1">Company Name</label> */}
                <input
                  type="text"
                  className="form-control form-control-sm"
                  name="company_name"
                  value={formData.company_name || ''}
                  onChange={handleChange}
                />

                {/* Show loaded conflict info banner */}
                {mode === 'create' && loadedFromConflict && (
                  <div className="alert alert-success alert-sm py-1 px-2 mt-1 mb-0" style={{ fontSize: '0.75rem' }}>
                    <i className="fas fa-check-circle me-1"></i>
                    Loaded data from {loadedFromConflict.type}: <strong>{loadedFromConflict.name}</strong>
                  </div>
                )}

                {/* Conflict Dropdown */}
                {mode === 'create' && checkingConflicts && (
                  <div className="small text-muted mt-1">
                    <i className="fas fa-spinner fa-spin me-1"></i>
                    Checking for duplicates...
                  </div>
                )}

                  {mode === 'create' && conflicts && (() => {
                    const allConflicts = [
                      ...(conflicts.prospects || []).map(prospect => ({
                        id: prospect.id,
                        uniqueKey: `prospect-${prospect.id}`,
                        type: 'Prospect',
                        badge: 'info',
                        name: prospect.full_name || 'Unknown Prospect'
                      })),
                      ...(conflicts.leads || []).map(lead => ({
                        id: lead.id,
                        uniqueKey: `lead-${lead.id}`,
                        type: 'Lead',
                        badge: 'success',
                        name: lead.company || lead.organization__name || 'Unknown Company'
                      })),
                      ...(conflicts.opportunities || []).map(opp => ({
                        id: opp.id,
                        uniqueKey: `opp-${opp.id}`,
                        type: 'Opportunity',
                        badge: 'warning',
                        name: opp.company || opp.organization__name || 'Unknown Company'
                      }))
                    ].sort((a, b) => a.name.localeCompare(b.name));

                    if (allConflicts.length === 0) return null;

                    return (
                      <div className="mt-1">
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <small className="text-warning">
                            <i className="fas fa-exclamation-triangle me-1"></i>
                            <strong>{allConflicts.length} match{allConflicts.length !== 1 ? 'es' : ''} found - Click to load</strong>
                          </small>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-warning py-0 px-2"
                            style={{ fontSize: '0.75rem' }}
                            onClick={() => setShowAllConflicts(!showAllConflicts)}
                          >
                            {showAllConflicts ? 'Collapse' : 'Expand'}
                          </button>
                        </div>

                        {!showAllConflicts ? (
                          <div style={{ border: '1px solid #ffc107', borderRadius: '4px', overflow: 'hidden' }}>
                            {allConflicts.slice(0, 4).map(item => (
                              <div
                                key={item.uniqueKey}
                                onClick={() => handleConflictClick(item.type, item.id, item.name)}
                                className="d-flex align-items-center p-2"
                                style={{
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f0f0f0',
                                  backgroundColor: loadedFromConflict?.uniqueKey === item.uniqueKey ? '#d1ecf1' : '#fffbf0',
                                  transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                  if (loadedFromConflict?.uniqueKey !== item.uniqueKey) {
                                    e.currentTarget.style.backgroundColor = '#fff3cd';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (loadedFromConflict?.uniqueKey !== item.uniqueKey) {
                                    e.currentTarget.style.backgroundColor = '#fffbf0';
                                  }
                                }}
                              >
                                <span className={`badge bg-${item.badge} me-2`} style={{ fontSize: '0.7rem', minWidth: '70px' }}>
                                  {item.type}
                                </span>
                                <div className="flex-grow-1" style={{ fontSize: '0.85rem' }}>
                                  <div className="fw-bold">{item.name}</div>
                                </div>
                                {loadedFromConflict?.uniqueKey === item.uniqueKey && (
                                  <i className="fas fa-check-circle text-success ms-2"></i>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ maxHeight: '250px', overflowY: 'auto', border: '1px solid #ffc107', borderRadius: '4px', padding: '4px', backgroundColor: '#fffbf0' }}>
                            {allConflicts.map(item => (
                              <div
                                key={item.uniqueKey}
                                onClick={() => handleConflictClick(item.type, item.id, item.name)}
                                className="d-flex align-items-start p-2 mb-1"
                                style={{
                                  cursor: 'pointer',
                                  borderRadius: '4px',
                                  backgroundColor: loadedFromConflict?.uniqueKey === item.uniqueKey ? '#d1ecf1' : 'transparent',
                                  transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                  if (loadedFromConflict?.uniqueKey !== item.uniqueKey) {
                                    e.currentTarget.style.backgroundColor = '#fff3cd';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (loadedFromConflict?.uniqueKey !== item.uniqueKey) {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }
                                }}
                              >
                                <span className={`badge bg-${item.badge} me-2`} style={{ fontSize: '0.7rem', minWidth: '70px' }}>
                                  {item.type}
                                </span>
                                <div className="flex-grow-1" style={{ fontSize: '0.85rem' }}>
                                  <div className="fw-bold">{item.name}</div>
                                </div>
                                {loadedFromConflict?.uniqueKey === item.uniqueKey && (
                                  <i className="fas fa-check-circle text-success ms-2"></i>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
              </div>
            </div>
          )}
        </div>

        {/* Prospect/Contact Information */}
        <div className="card  p-3 mb-2">
          <div
            className="d-flex card-title text-primary justify-content-between align-items-center mb-2 pt-2"
            style={{ cursor: 'pointer' }}
            onClick={() => toggleSection('prospectInfo')}
          >
            <span>
              Contact Person
              {mode === 'edit' && fullEntityData?.prospects && fullEntityData.prospects.length > 0 && (
                <span className="badge bg-primary ms-2">{fullEntityData.prospects.length}</span>
              )}
              {mode === 'create' && prospects.length > 0 && (
                <span className="badge bg-primary ms-2">{prospects.length}</span>
              )}
            </span>
            <i className={`fas fa-chevron-${expandedSections.prospectInfo ? 'up' : 'down'}`}></i>
          </div>
          {expandedSections.prospectInfo && (
            <div className="">
              <ProspectManager
                entity={fullEntityData}
                entityType={entityType}
                isCreateMode={mode === 'create'}
                onChange={(updatedProspects) => {
                  setProspects(updatedProspects);
                }}
                onError={(errorMsg) => {
                  setError(errorMsg);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            </div>
          )}
        </div>

        {/* Lead Tracker */}
        <div className="mb-2">
          <LeadAndOpportunityTracker
            entity={fullEntityData}
            isCreateMode={mode === 'edit' ? (false) : (true)}
            onUpdate={(updatedEntity) => {
              setFullEntityData(updatedEntity);
              // Propagate update to parent (SalesTablePage) to update Redux
              if (onSave) {
                onSave(updatedEntity);
              }
            }}
            onChange={(trackerData) => {
              setFormData(prev => ({ ...prev, ...trackerData }));
            }}
            entityType={entityType}
          />
        </div>

        {/* Job/Opportunity Details */}
        <div className="card p-3 mb-2">
          <div
            className="d-flex card-title text-primary justify-content-between align-items-center mb-2 pt-2"
            style={{ cursor: 'pointer' }}
            onClick={() => toggleSection('jobDetails')}
          >
            <span>{entityType === 'lead' ? 'Job Details' : 'Opportunity Details'}</span>
            <i className={`fas fa-chevron-${expandedSections.jobDetails ? 'up' : 'down'}`}></i>
          </div>
          {expandedSections.jobDetails && (
            <div className="">
              <div className="mb-2">
                <label className="form-label form-label-sm mb-1">{entityType === 'lead' ? 'Job Title' : 'Opportunity Title'}</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  name="job_title"
                  value={formData.job_title || ''}
                  onChange={handleChange}
                  placeholder="e.g., Senior Software Engineer"
                />
              </div>

              <div className="mb-2">
                <label className="form-label form-label-sm mb-1">Description</label>
                <textarea
                  className="form-control form-control-sm"
                  name="description"
                  value={formData.description || ''}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Enter description..."
                />
              </div>

              <div className="row g-2 mb-2">
                <div className="col-md-6">
                  <label className="form-label form-label-sm mb-1">Location</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="location"
                    value={formData.location || ''}
                    onChange={handleChange}
                    placeholder="San Francisco, CA"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label form-label-sm mb-1">Employment Type</label>
                  <select
                    className="form-select form-select-sm"
                    name="employment_type"
                    value={formData.employment_type || ''}
                    onChange={handleChange}
                  >
                    <option value="">Select Type</option>
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Contract">Contract</option>
                    <option value="Temporary">Temporary</option>
                    <option value="Internship">Internship</option>
                  </select>
                </div>
              </div>

              <div className="row g-2 mb-2">
                <div className="col-md-4">
                  <label className="form-label form-label-sm mb-1">
                    <input
                      type="checkbox"
                      name="remote"
                      checked={formData.remote || false}
                      onChange={handleChange}
                      className="me-1"
                    />
                    Remote
                  </label>
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-sm mb-1">
                    <input
                      type="checkbox"
                      name="hybrid"
                      checked={formData.hybrid || false}
                      onChange={handleChange}
                      className="me-1"
                    />
                    Hybrid
                  </label>
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-sm mb-1">Date Posted</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    name="date_posted"
                    value={formData.date_posted || ''}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="row g-2 mb-0">
                <div className="col-md-6">
                  <label className="form-label form-label-sm mb-1">Salary Range</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="salary_string"
                    value={formData.salary_string || ''}
                    onChange={handleChange}
                    placeholder="$80k-$120k"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label form-label-sm mb-1">URL</label>
                  <input
                    type="url"
                    className="form-control form-control-sm"
                    name="url"
                    value={formData.url || ''}
                    onChange={handleChange}
                    placeholder="https://..."
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Organization Details */}
        <div className="card p-3 mb-2">
          <div
            className="d-flex card-title text-primary justify-content-between align-items-center mb-2 pt-2"
            style={{ cursor: 'pointer' }}
            onClick={() => toggleSection('organizationDetails')}
          >
            <span>Organization Details</span>
            <i className={`fas fa-chevron-${expandedSections.organizationDetails ? 'up' : 'down'}`}></i>
          </div>
          {expandedSections.organizationDetails && (
            <div className="">
              <div className="row g-2 mb-2">
                <div className="col-md-6">
                  <label className="form-label form-label-sm mb-1">Company Domain</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="company_domain"
                    value={formData.company_domain || ''}
                    onChange={handleChange}
                    placeholder="company.com"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label form-label-sm mb-1">Company URL</label>
                  <input
                    type="url"
                    className="form-control form-control-sm"
                    name="company_url"
                    value={formData.company_url || ''}
                    onChange={handleChange}
                    placeholder="https://company.com"
                  />
                </div>
              </div>

              <div className="row g-2 mb-2">
                <div className="col-md-6">
                  <label className="form-label form-label-sm mb-1">Company Phone</label>
                  <input
                    type="tel"
                    className="form-control form-control-sm"
                    name="company_phone"
                    value={formData.company_phone || ''}
                    onChange={handleChange}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label form-label-sm mb-1">Company Email</label>
                  <input
                    type="email"
                    className="form-control form-control-sm"
                    name="company_email"
                    value={formData.company_email || ''}
                    onChange={handleChange}
                    placeholder="contact@company.com"
                  />
                </div>
              </div>

              <div className="mb-2">
                <label className="form-label form-label-sm mb-1">Company Description</label>
                <textarea
                  className="form-control form-control-sm"
                  name="company_description"
                  value={formData.company_description || ''}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Brief company description..."
                />
              </div>

              <div className="row g-2 mb-0">
                <div className="col-md-4">
                  <label className="form-label form-label-sm mb-1">Company Size</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="company_size"
                    value={formData.company_size || ''}
                    onChange={handleChange}
                    placeholder="1-50"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-sm mb-1">Industry</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="company_industry"
                    value={formData.company_industry || ''}
                    onChange={handleChange}
                    placeholder="Healthcare, Tech, etc."
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-sm mb-1">Revenue</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="company_revenue"
                    value={formData.company_revenue || ''}
                    onChange={handleChange}
                    placeholder="$1M-$10M"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Address Information */}
        <div className="card p-3 mb-2">
          <div
            className="d-flex card-title text-primary justify-content-between align-items-center mb-2 pt-2"
            style={{ cursor: 'pointer' }}
            onClick={() => toggleSection('addressInfo')}
          >
            <span>Address Information</span>
            <i className={`fas fa-chevron-${expandedSections.addressInfo ? 'up' : 'down'}`}></i>
          </div>
          {expandedSections.addressInfo && (
            <div className="">
              <div className="mb-2">
                <label className="form-label form-label-sm mb-1">Street Address</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  name="location_street"
                  value={formData.location_street || ''}
                  onChange={handleChange}
                  placeholder="123 Main St"
                />
              </div>

              <div className="row g-2 mb-2">
                <div className="col-md-6">
                  <label className="form-label form-label-sm mb-1">City</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="location_city"
                    value={formData.location_city || ''}
                    onChange={handleChange}
                    placeholder="City"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label form-label-sm mb-1">State/Province</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="location_state"
                    value={formData.location_state || ''}
                    onChange={handleChange}
                    placeholder="State"
                  />
                </div>
              </div>

              <div className="row g-2 mb-0">
                <div className="col-md-6">
                  <label className="form-label form-label-sm mb-1">ZIP/Postal Code</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="location_zip"
                    value={formData.location_zip || ''}
                    onChange={handleChange}
                    placeholder="12345"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label form-label-sm mb-1">Country</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="location_country"
                    value={formData.location_country || ''}
                    onChange={handleChange}
                    placeholder="Country"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        {/* System Information - Only in Edit Mode */}
        {mode === 'edit' && fullEntityData && (
          <div className="card p-3 mb-2">
            <div
              className="d-flex card-title text-primary justify-content-between align-items-center mb-2 pt-2"
              style={{ cursor: 'pointer' }}
              onClick={() => toggleSection('systemInfo')}
            >
              <span>System Information</span>
              <i className={`fas fa-chevron-${expandedSections.systemInfo ? 'up' : 'down'}`}></i>
            </div>
            {expandedSections.systemInfo && (
              <div className="">
                <div className="row g-2 mb-2">
                  <div className="col-md-6">
                    <label className="form-label form-label-sm mb-1">Created At</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={fullEntityData.created_at ? new Date(fullEntityData.created_at).toLocaleString() : 'N/A'}
                      disabled
                      readOnly
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label form-label-sm mb-1">Last Updated</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={fullEntityData.updated_at ? new Date(fullEntityData.updated_at).toLocaleString() : 'N/A'}
                      disabled
                      readOnly
                    />
                  </div>
                </div>

                <div className="row g-2 mb-2">
                  <div className="col-md-6">
                    <label className="form-label form-label-sm mb-1">Last Contacted</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      name="last_contacted_date"
                      value={formData.last_contacted_date || ''}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label form-label-sm mb-1">Follow Up Date</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      name="follow_up_date"
                      value={formData.follow_up_date || ''}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="row g-2 mb-0">
                  <div className="col-md-6">
                    <label className="form-label form-label-sm mb-1">Record ID</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={fullEntityData.id || 'N/A'}
                      disabled
                      readOnly
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label form-label-sm mb-1">Created By</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={fullEntityData.created_by?.username || fullEntityData.created_by || 'N/A'}
                      disabled
                      readOnly
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Admin & Notes Section */}
        <div className="card p-3 mb-2">
          <div
            className="d-flex card-title text-primary justify-content-between align-items-center mb-2 pt-2"
            style={{ cursor: 'pointer' }}
            onClick={() => toggleSection('additionalInfo')}
          >
            <span>Additional Information</span>
            <i className={`fas fa-chevron-${expandedSections.additionalInfo ? 'up' : 'down'}`}></i>
          </div>
          {expandedSections.additionalInfo && (
            <div className="">
              <div className="mb-2">
                <label className="form-label form-label-sm mb-1">Which Organization Owns This {entityType === 'lead' ? ('Lead?'):('Opportunity?')}</label>
                <select
                  className="form-select form-select-sm"
                  name="organization_id"
                  value={formData.organization_id || ''}
                  onChange={handleChange}
                >
                  <option value="">Select Organization</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div>
                <label className="form-label form-label-sm mb-1">Active Status</label>
                <p className="text-muted small mb-0">
                  {formData.is_active ?
                    'This record is active and visible in the main list' :
                    'This record is archived and hidden from the main list'}
                </p>
              </div>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="is_active"
                  id="is_active_switch"
                  checked={formData.is_active !== false}
                  onChange={handleChange}
                  style={{ transform: 'scale(1.5)', marginRight: '10px' }}
                />
              </div>
            </div>
              {entityType === 'lead' && (
                <div className="mb-2">
                  <label className="form-label form-label-sm mb-1">External ID</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="external_id"
                    value={formData.external_id || ''}
                    onChange={handleChange}
                    placeholder="External system ID"
                  />
                </div>
              )}

              <div className="mb-0">
                <label className="form-label form-label-sm mb-1">Notes</label>
                <textarea
                  className="form-control form-control-sm"
                  name="notes"
                  value={formData.notes || ''}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Enter notes..."
                />
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="d-flex gap-2 mt-3">
          <button
            type="submit"
            className="btn btn-primary btn-sm"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {mode === 'create' ? 'Creating...' : 'Saving...'}
              </>
            ) : (
              mode === 'create' ? 'Create' : 'Save Changes'
            )}
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
        </div>
      </form>
    </SidebarPanel>
  );
};

export default EntityEditor;
